import { useState, lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { RouteChangeUpdater } from "@/components/RouteChangeUpdater";
import { useSessionCutoff } from "@/hooks/use-session-cutoff";
import { usePushSubscriptionHealer } from "@/hooks/use-push-notifications";
import { SwNavigateBridge } from "@/components/SwNavigateBridge";
import { PasswordRecoveryRedirect } from "@/components/PasswordRecoveryRedirect";
import { ChatOverlayProvider } from "@/contexts/ChatOverlayContext";
import { StorageSettingsProvider } from "@/contexts/StorageSettingsContext";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PageLoadingSpinner } from "@/components/PageLoadingSpinner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import { useAppVersionTracker } from "@/hooks/use-app-version-tracker";
import { retryDynamicImport } from "@/lib/staleChunkReload";

declare global {
  interface Window {
    supabase: typeof supabase;
  }
}

const ChatOverlay = lazy(
  retryDynamicImport(() =>
    import("@/components/messaging/ChatOverlay").then((module) => ({
      default: module.ChatOverlay,
    }))
  )
);

function SessionChatOverlay() {
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setHasSession(Boolean(data.session));
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setHasSession(Boolean(session));
    });
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (!hasSession) return null;
  return (
    <Suspense fallback={null}>
      <ChatOverlay />
    </Suspense>
  );
}

// Expose supabase to window for console testing (development only)
if (import.meta.env.DEV && typeof window !== "undefined") {
  window.supabase = supabase;
  console.log("💡 Supabase client available at window.supabase for testing");
}

// Eagerly load small pages (<10KB)
import Index from "./pages/Index";
import SparkRating from "./pages/SparkRating";
import ColorReference from "./pages/ColorReference";
import WaveDividerDemo from "./pages/WaveDividerDemo";
import NotFound from "./pages/NotFound";
import ResetPassword from "./pages/ResetPassword";
import Calendar from "./pages/Calendar";
// Lazy load heavy dashboards
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
// Patricia's own dashboard: For The Boss and William's work. Boss and dev only.
const BossDashboard = lazy(() => import("./pages/BossDashboard"));
const RegistrationForm = lazy(() => import("./pages/RegistrationForm"));
const InsuranceCardForm = lazy(() => import("./pages/InsuranceCardForm"));
const InsuranceCardPrint = lazy(() => import("./pages/InsuranceCardPrint"));
const ChildPacketPrint = lazy(() => import("./pages/ChildPacketPrint"));
const IncidentReport = lazy(() => import("./pages/IncidentReport"));
const IncidentReportPrint = lazy(() => import("./pages/IncidentReportPrint"));
const WitnessStatement = lazy(() => import("./pages/WitnessStatement"));
const SignHandoff = lazy(() => import("./pages/SignHandoff"));
const CosignLink = lazy(() => import("./pages/CosignLink"));
const VerifyForm = lazy(() => import("./pages/VerifyForm"));
const IncidentByCode = lazy(() => import("./pages/IncidentByCode"));
const DocumentView = lazy(() => import("./pages/DocumentView"));
const SampleFormView = lazy(() => import("./pages/SampleFormView"));
// Development only: the Signed Documents card on fixture children, no login needed.
const SignedDocumentsPreview = lazy(() => import("@/pages/dev/SignedDocumentsPreview"));
const UiPreview = lazy(() => import("@/pages/dev/UiPreview"));

// Lazy load large program pages (15KB-31KB each)
const MeetOurTeam = lazy(() => import("./pages/MeetOurTeam"));
// Database-driven program pages (Programs Editor) — the only program template.
const ProgramPage = lazy(() => import("./pages/ProgramPage"));

// Lazy load other medium-large pages (10KB-20KB)
const ChildrensHourPoem = lazy(() => import("./pages/ChildrensHourPoem"));
const ScheduleTour = lazy(() => import("./pages/ScheduleTour"));
const Auth = lazy(() => import("./pages/Auth"));
const AccountSuspended = lazy(() => import("./pages/AccountSuspended"));
const Settings = lazy(() => import("./pages/Settings"));
const ManualPage = lazy(() => import("./pages/ManualPage"));
const SplashScreenGenerator = lazy(() => import("./pages/SplashScreenGenerator"));
const SchoolBuildingPhotos = lazy(() => import("./pages/SchoolBuildingPhotos"));
const Foundation = lazy(() => import("./pages/Foundation"));
const FoundationApply = lazy(() => import("./pages/FoundationApply"));
const FoundationRequestFunding = lazy(() => import("./pages/FoundationRequestFunding"));
const FoundationAuction = lazy(() => import("./pages/FoundationAuction"));
const FoundationJoin = lazy(() => import("./pages/FoundationJoin"));
const FoundationFAQ = lazy(() => import("./pages/FoundationFAQ"));
const FoundationLeadership = lazy(() => import("./pages/FoundationLeadership"));
const Donate = lazy(() => import("./pages/Donate"));

// Foundation Admin Pages (separate from main Admin Dashboard)
const AdminAidApplications = lazy(() => import("./pages/AdminAidApplications"));
const AdminFundingRequests = lazy(() => import("./pages/AdminFundingRequests"));
const AdminDocuments = lazy(() => import("./pages/AdminDocuments"));

/*
 * Coming back to a tab re-reads what has gone stale.
 *
 * This used to be off, on the reasonable-sounding grounds that a window
 * regaining focus is not news. The trouble is what it left: a screen open in
 * a background tab keeps its rows for half an hour, and the realtime
 * subscriptions that were supposed to cover the gap only hear what happens
 * while their socket is up - a sleeping laptop or a car screen going dark
 * misses the event outright, and nothing is replayed on reconnect.
 *
 * So a page left open overnight went on showing yesterday. On 23 Sep 2026 a
 * child sat in the review list all evening after the office had accepted
 * him; the moment something forced a refetch he vanished, and it looked for
 * all the world as though the person watching had just accepted him by
 * accident. Twenty-three screens carry those subscriptions and every one had
 * the same blind spot.
 *
 * This is cheap because staleTime still governs it: focus only refetches
 * what is actually stale, and the eighty-odd queries that set their own
 * staleTime keep their own answer to that. Refetching on reconnect is on by
 * default and covers the other half - the network coming back while the tab
 * was in front of you the whole time.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,  // 2 minutes - data considered fresh
      gcTime: 30 * 60 * 1000,    // 30 minutes - keep in cache longer
      refetchOnWindowFocus: true,
    },
  },
});

// The last account the service worker's photo cache was filled for.
//
// The worker caches a signed photo URL with the signature stripped from the
// key, which is what lets one download serve a picture for the rest of the
// month — and also what stops the link expiring, being revoked, or belonging
// to somebody else from making any difference. So the cache is emptied when
// the person changes: at sign-out, and at a sign-in by a different account.
// Two families on one iPad is the ordinary case, not the exotic one.
const LAST_ACCOUNT_KEY = "cha-last-account";

function purgePrivateCaches() {
  navigator.serviceWorker?.ready
    .then((reg) => reg.active?.postMessage({ type: "CLEAR_PRIVATE_CACHE" }))
    .catch(() => { /* No worker registered; nothing was cached. */ });
}

// Role-gated UI (animated wave, dev menus, admin sub-tabs) reads cached role
// queries that otherwise go stale across sign-in/sign-out — a fresh sign-in
// kept showing the signed-out experience until a manual reload. Refresh the
// identity caches the moment auth actually changes (not on routine token
// refreshes, which fire hourly). The photo cache is emptied here too, for the
// reason above.
supabase.auth.onAuthStateChange((event, session) => {
  if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
    queryClient.invalidateQueries({ queryKey: ["auth-user-id"] });
    queryClient.invalidateQueries({ queryKey: ["user-roles"] });
  }

  if (event === "SIGNED_OUT") {
    try { localStorage.removeItem(LAST_ACCOUNT_KEY); } catch { /* private mode */ }
    purgePrivateCaches();
  } else if (event === "SIGNED_IN" && session?.user?.id) {
    let previous: string | null = null;
    try {
      previous = localStorage.getItem(LAST_ACCOUNT_KEY);
      localStorage.setItem(LAST_ACCOUNT_KEY, session.user.id);
    } catch {
      // Storage refused, so the previous account is unknown. Clear rather than
      // guess: an unnecessary re-download costs a moment, the other way round
      // shows one family another family's photographs.
      purgePrivateCaches();
      return;
    }
    if (previous && previous !== session.user.id) purgePrivateCaches();
  }
});

// import AuthDebugOverlay from "@/components/AuthDebugOverlay";

/**
 * Redirect that keeps the query string.
 *
 * <Navigate to="/x" /> drops it, and the form addresses carry ?child=, which
 * is what decides which of a family's children the form opens for. A parent
 * of three following an old link would otherwise land on their eldest's.
 */
const KeepQuery = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  // Reports which build each user is running, for the admin App Version Tracker.
  // Read-only: updating to a new build is the service worker's job (src/main.tsx).
  useAppVersionTracker();

  // Drops sessions issued before an admin's "sign out everyone" cutoff.
  useSessionCutoff();

  // Restores this device's push subscription if it was lost (e.g. by an old
  // Clear Cache & Reload unregistering the worker). Never prompts; respects
  // an explicit opt-out.
  usePushSubscriptionHealer();


  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {isLoading && <LoadingScreen onComplete={() => setIsLoading(false)} />}
        <BrowserRouter>
          <ChatOverlayProvider>
          <ScrollToTop />
          <RouteChangeUpdater />
          <SwNavigateBridge />
          <PasswordRecoveryRedirect />
          {/* <AuthDebugOverlay /> */}
          <ErrorBoundary label="The app">
          <Suspense fallback={<PageLoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Index />} />
              {/* Program pages: every program URL renders the database-driven
                  template (Programs Editor). The handcrafted pages are gone —
                  the old URLs stay, serving the published program of the same
                  slug, so existing links and search results keep working. */}
              <Route path="/infant-program" element={<ProgramPage slug="infant-program" />} />
              <Route path="/toddler-program" element={<ProgramPage slug="toddler-program" />} />
              <Route path="/early-toddler-program" element={<ProgramPage slug="early-toddler-program" />} />
              <Route path="/middle-toddler-program" element={<ProgramPage slug="middle-toddler-program" />} />
              <Route path="/older-toddler-program" element={<ProgramPage slug="older-toddler-program" />} />
              <Route path="/pre-preschool-program" element={<ProgramPage slug="pre-preschool-program" />} />
              {/* Room A/B consolidated into one page per program — keep the old
                  URLs working for existing links and search results. */}
              <Route path="/pre-preschool-program-b" element={<Navigate to="/pre-preschool-program" replace />} />
              <Route path="/preschool-program" element={<ProgramPage slug="preschool-program" />} />
              <Route path="/prekindergarten-program" element={<ProgramPage slug="prekindergarten-program" />} />
              <Route path="/preparatory-kindergarten-program" element={<ProgramPage slug="preparatory-kindergarten-program" />} />
              <Route path="/preparatory-kindergarten-program-b" element={<Navigate to="/preparatory-kindergarten-program" replace />} />
              <Route path="/kindergarten-program" element={<ProgramPage slug="kindergarten-program" />} />
              <Route path="/meet-our-team" element={<MeetOurTeam />} />
              <Route path="/spark-rating" element={<SparkRating />} />
              <Route path="/color-reference" element={<ColorReference />} />
              <Route path="/childrens-hour-poem" element={<ChildrensHourPoem />} />
              <Route path="/schedule-tour" element={<ScheduleTour />} />
              <Route path="/wave-demo" element={<WaveDividerDemo />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/account-suspended" element={<AccountSuspended />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/settings" element={<Settings />} />
              {/* The dashboard manuals: /manual/parent, /manual/teacher, /manual/admin. */}
              <Route path="/manual/:audience" element={<ManualPage />} />
              <Route path="/parent-dashboard" element={<ParentDashboard />} />
              {/* Form signing (docs/FORM_SIGNING_PLAN.md).

                  THE SHORT FORMS NOW REDIRECT TO THEIR PACKETS. Both cutovers
                  on 1 Sep moved the only `forms` rows to the packet
                  addresses, which means registration_form_id() no longer
                  resolves either short kind: a parent arriving on one of
                  these addresses could still fill the form in and sign it,
                  the seal would succeed, they would be handed a verification
                  code - and no submission row would ever reach the office,
                  because the lookup returned NULL. Serving the form that is
                  actually being collected is the only version of this with no
                  silent failure in it.

                  The addresses stay alive rather than 404ing: they are in
                  emails already sent, in the office's bookmarks, and in the
                  `forms` rows of anyone who has not reloaded. The query
                  string comes along - ?child= decides WHOSE form opens, and
                  dropping it hands the family their eldest child's. */}
              <Route path="/forms/registration-it7" element={<KeepQuery to="/forms/registration-it7-packet" />} />
              {/* The address it was served at while the form printed the wrong
                  number. */}
              <Route path="/forms/registration-it9" element={<KeepQuery to="/forms/registration-it7-packet" />} />
              {/* Where the Infant/Toddler form lived before the two forms were
                  given matching addresses. */}
              <Route path="/forms/registration" element={<KeepQuery to="/forms/registration-it7-packet" />} />
              {/* The Preschool-Kindergarten short form's own address. */}
              <Route path="/forms/registration-mc4" element={<KeepQuery to="/forms/registration-mc4-packet" />} />
              {/* The insurance card as a form of its own, so the office can
                  require it and preview it like any other, and so a parent
                  can be linked straight to it. */}
              <Route path="/forms/insurance-card" element={<InsuranceCardForm />} />
              {/* The uploaded card on a Letter sheet, for the office file and
                  for anyone who needs it on paper. */}
              <Route path="/forms/insurance-card/print" element={<InsuranceCardPrint />} />
              <Route path="/print/packet/:code" element={<ChildPacketPrint />} />
              {/* The Infant/Toddler packet, still being built. Reachable by
                  URL only until the office points a forms row at it. */}
              <Route
                path="/forms/registration-it7-packet"
                element={<RegistrationForm formKind="it9-full-registration" />}
              />
              {/* Its address while the packet printed the wrong number. */}
              <Route
                path="/forms/registration-it9-packet"
                element={<RegistrationForm formKind="it9-full-registration" />}
              />
              {/* The Preschool-K packet, still being built. Reachable by URL
                  only until the office points a forms row at it. */}
              <Route
                path="/forms/registration-mc4-packet"
                element={<RegistrationForm formKind="mc4-full-registration" />}
              />
              <Route path="/sign/:token" element={<SignHandoff />} />
              {/* A co-parent with no portal account, signing from the link
                  the office emailed them. Public by design; the page asks
                  for the child's date of birth before it shows anything. */}
              <Route path="/cosign/:token" element={<CosignLink />} />
              <Route path="/documents/:code" element={<DocumentView />} />
              <Route path="/admin/sample-form" element={<SampleFormView />} />
              {import.meta.env.DEV && (
                <Route path="/dev/signed-documents" element={<SignedDocumentsPreview />} />
              )}
              {import.meta.env.DEV && <Route path="/dev/ui" element={<UiPreview />} />}
              <Route path="/verify" element={<VerifyForm />} />
              <Route path="/verify/:code" element={<VerifyForm />} />
              {/* Where the QR on a printed accident report lands. The page
                  decides by the account: the office, the child's own family,
                  or nobody. */}
              <Route path="/incident/:code" element={<IncidentByCode />} />
              <Route
                path="/admin"
                element={
                  <StorageSettingsProvider>
                    <AdminDashboard />
                  </StorageSettingsProvider>
                }
              />
              <Route path="/boss" element={<BossDashboard />} />
              <Route
                path="/teacher-dashboard"
                element={
                  <StorageSettingsProvider>
                    <TeacherDashboard />
                  </StorageSettingsProvider>
                }
              />
              {/* Reporting an accident or injury. A page rather than a
                  dialog: a teacher does this standing up on a phone. */}
              <Route path="/report-injury" element={<IncidentReport />} />
              <Route path="/report-injury/:id" element={<IncidentReport />} />
              {/* The report on paper, filled in. ?copy=family drops the
                  office-only block. */}
              <Route path="/print/incident/:id" element={<IncidentReportPrint />} />
              {/* A witness writes her own statement from a link in an
                  email. No account: the token is the whole of the auth,
                  and she is deliberately shown none of the teacher's
                  account of what happened. */}
              <Route path="/witness/:token" element={<WitnessStatement />} />
              <Route path="/calendar/:calendarId" element={<Calendar />} />
              <Route path="/splash-screen-generator" element={<SplashScreenGenerator />} />
              <Route path="/school-building-photos" element={<SchoolBuildingPhotos />} />
              <Route path="/foundation" element={<Foundation />} />
              <Route path="/foundation/apply" element={<FoundationApply />} />
              <Route path="/foundation/request-funding" element={<FoundationRequestFunding />} />
              <Route path="/foundation/auction" element={<FoundationAuction />} />
              <Route path="/foundation/join" element={<FoundationJoin />} />
              <Route path="/foundation/faq" element={<FoundationFAQ />} />
              <Route path="/foundation/leadership" element={<FoundationLeadership />} />
              <Route path="/donate" element={<Donate />} />
              {/* Foundation Admin Routes (accessible by admin and chaf_admin) */}
              <Route path="/admin/aid-applications" element={<AdminAidApplications />} />
              <Route path="/admin/funding-requests" element={<AdminFundingRequests />} />
              <Route path="/admin/documents" element={<AdminDocuments />} />
              {/* Database-driven program pages (Programs Editor, parallel run) */}
              <Route path="/programs/:slug" element={<ProgramPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          <SessionChatOverlay />
          </ChatOverlayProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
